import * as XLSX from 'xlsx';
import ConfirmModal from "../components/ConfirmModal";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Payments from "./Payments";
import Broadcast from "./Broadcast";
import { pct, fmtTime } from "../utils/helpers";
import Admin from "./Admin";
const API = (process.env.REACT_APP_API_URL || "") + "";
const token = () => localStorage.getItem("token") || "";

async function apiFetch(path, opts = {}) {
  const r = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    let _errMsg = "Something went wrong";
    try {
      const _errJson = await r.json();
      _errMsg = _errJson.detail || _errJson.message || JSON.stringify(_errJson);
      // "Meeting Error: " prefix hatao — clean message dikho
      _errMsg = _errMsg.replace(/^Meeting Error:\s*/i, "");
    } catch { _errMsg = await r.text().catch(() => "Request failed"); }
    throw new Error(_errMsg);
  }
  return r.json();
}

function fmtCountdown(ts) {
  const diff = Math.max(0, Math.floor(ts - Date.now() / 1000));
  const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60;
  return `${h}h ${m}m ${s}s`;
}

function CopyUrlCell({ url, passcode }) {
  const [copied, setCopied] = useState(false);
  // Orders placed with ID + separate password store the passcode in its own
  // column — the raw zoom_url never has it embedded (see create_campaign in
  // routes/campaigns.py). Pasting just the bare link then prompts for the
  // password again, so build the full join link with ?pwd= baked in,
  // exactly like Zoom's own "Copy Invite Link" does.
  const fullUrl = (() => {
    if (!url) return "";
    if (!passcode || /[?&]pwd=/.test(url)) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}pwd=${encodeURIComponent(passcode)}`;
  })();
  const display = fullUrl.slice(0, 26) + (fullUrl.length > 26 ? "…" : "");
  const doCopy = (e) => {
    e.stopPropagation();
    if (!fullUrl) return;
    navigator.clipboard?.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span
      title={fullUrl}
      onClick={doCopy}
      style={{ fontSize: 11, fontWeight: 600, color: copied ? "#16a34a" : "#44403c", fontFamily: "'JetBrains Mono',monospace", cursor: fullUrl ? "pointer" : "default" }}
    >
      {copied ? "✓ Copied" : (display || "—")}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    running:   ["adm-badge-green", "Running",   "#16a34a"],
    joining:   ["adm-badge-blue",  "Joining",   "#b45309"],
    pending:   ["adm-badge-amber", "Pending",   "#d97706"],
    stopped:   ["adm-badge-red",   "Stopped",   "#dc2626"],
    scheduled: ["adm-badge-blue",  "Scheduled", "#b45309"],
    failed:    ["adm-badge-red",   "Failed",    "#dc2626"],
    waiting:       ["adm-badge-amber", "Waiting",       "#d97706"],
    provisioning:  ["adm-badge-blue",  "Provisioning", "#7c3aed"],
  };
  const [cls, lbl, col] = map[status] || map.stopped;
  return (
    <span className={`adm-badge ${cls}`} style={{ color: col, borderColor: col + "40", background: col + "15" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: col, flexShrink: 0, display: "inline-block" }} />
      {lbl}
    </span>
  );
}

function fmtReason(r) {
  if (!r) return null;
  const s = r.toLowerCase();
  if (s.includes("host_ended") || s.includes("host ended")) return "Host Ended";
  if (s.includes("meeting_full") || s.includes("at capacity")) return "Meeting Full";
  if (s.includes("does not exist") || s.includes("invalid meeting")) return "Invalid Link";
  if (s.includes("ended") || s.includes("over")) return "Meeting Ended";
  if (s.includes("stopped while waiting") || s.includes("waiting for meeting")) return "Meeting Offline";
  if (s.includes("bots aren") || s.includes("not allowed")) return "Bots Blocked";
  return r.slice(0, 28);
}

const TDstyle = { padding: "11px 14px", verticalAlign: "middle" };

function AB({ c, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: c + "14", color: c, border: `1px solid ${c}32`,
      borderRadius: 7, padding: "5px 14px", fontSize: 11, fontWeight: 700,
      cursor: "pointer", fontFamily: "inherit",
    }}>{children}</button>
  );
}

function Pager({ page, total, set }) {
  if (total <= 1) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      {[["‹", -1], ["›", 1]].map(([sym, d]) => {
        const disabled = d === -1 ? page === 0 : page === total - 1;
        return (
          <button key={sym} onClick={() => !disabled && set(p => p + d)} style={{
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "#78716c",
            borderRadius: 6, width: 28, height: 28, cursor: disabled ? "default" : "pointer", fontSize: 15,
            opacity: disabled ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
          }}>{sym}</button>
        );
      })}
      <span style={{ fontSize: 11, color: "#a8a29e", minWidth: 32, textAlign: "center" }}>{page + 1}/{total}</span>
    </div>
  );
}

/* ── Mobile Campaign Card ── */
const MobileCampCard = React.memo(function MobileCampCard({ c, onStop, stopReason }) {
  const joined = c.bots_joined || c.active || 0;
  const total = c.total_bots || 1;
  const pct2 = Math.min(100, Math.round((joined / total) * 100));
  const isRun = ["running","joining","provisioning","pending"].includes(c.status);
  const nameLabel = c.name_type === "indian" ? "🇮🇳 Hindi" : c.name_type === "foreign" ? "🌍 English" : "✏️ Custom";
  const typeLabel = c.meeting_type === "webinar" ? "🎙 Webinar" : c.meeting_type === "profile_pic" ? "👤 Profile" : "👥 Normal";
  const hasReact = c.floating_emoji || c.participant_reactions;
  const startTime = c.started_at ? fmtTime(c.started_at) : c.schedule_at ? "⏰ " + fmtTime(c.schedule_at) : "—";
  return (
    <div style={{
      background: isRun ? "rgba(180,83,9,0.04)" : "rgba(255,255,255,0.02)",
      border: "1px solid " + (isRun ? "rgba(180,83,9,0.2)" : "rgba(255,255,255,0.07)"),
      borderRadius: 12, padding: "14px", marginBottom: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14, fontWeight: 700, color: isRun ? "#b45309" : "#b45309", textShadow: isRun ? "0 0 10px rgba(180,83,9,0.7)" : "none" }}>
            #{c.name || "—"}
          </div>
          <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{(c.zoom_url||"").slice(0, 28)}{(c.zoom_url||"").length > 28 ? "…" : ""}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <StatusBadge status={c.status} />
          {!isRun && stopReason && <div style={{ fontSize: 9, color: "#dc2626", background: "rgba(220,38,38,.1)", border: "1px solid rgba(220,38,38,.25)", borderRadius: 20, padding: "2px 7px", marginTop: 4, display: "inline-block" }}>⚠ {fmtReason(stopReason)}</div>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div style={{ textAlign: "center", background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 4px" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#292524" }}>{joined}<span style={{ color: "#d6d3d1", fontWeight: 400 }}>/{total}</span></div>
          <div style={{ fontSize: 9, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Members</div>
          {isRun && <div style={{ height: 3, background: "rgba(255,255,255,.06)", borderRadius: 3, marginTop: 5, overflow: "hidden" }}><div style={{ height: "100%", width: pct2 + "%", background: "linear-gradient(90deg,#b45309,#7c3aed)", borderRadius: 3 }} /></div>}
        </div>
        <div style={{ textAlign: "center", background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 4px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#78716c" }}>{c.leave_after_mins > 0 ? c.leave_after_mins + "m" : "∞"}</div>
          <div style={{ fontSize: 9, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Leave</div>
        </div>
        <div style={{ textAlign: "center", background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 4px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: hasReact ? "#16a34a" : "#dc2626" }}>{hasReact ? "✓" : "✗"}</div>
          <div style={{ fontSize: 9, color: "#a8a29e", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>React</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.05)", borderRadius: 5, padding: "3px 7px" }}>{nameLabel}</span>
          <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.05)", borderRadius: 5, padding: "3px 7px" }}>{typeLabel}</span>
        </div>
        {isRun && <div style={{ display: "flex", gap: 5, flexShrink: 0 }}><AB c="#dc2626" onClick={onStop}>⏹ Stop</AB></div>}
      </div>
    </div>
  );
}, (prev, next) =>
  prev.c.id === next.c.id &&
  prev.c.status === next.c.status &&
  prev.c.bots_joined === next.c.bots_joined &&
  prev.c.active === next.c.active &&
  prev.c.active_bots === next.c.active_bots &&
  prev.stopReason === next.stopReason
);

/* ── Mobile History Card ── */
function MobileHistoryCard({ h }) {
  const meetNum = h.campaign_name || h.name || "—";
  const nameLabel = h.name_type === "indian" ? "🇮🇳 Hindi" : h.name_type === "foreign" ? "🌍 English" : "✏️ Custom";
  const typeLabel = h.meeting_type === "webinar" ? "🎙 Webinar" : h.meeting_type === "profile_pic" ? "👤 Profile" : "👥 Normal";
  const hasReact = h.floating_emoji || h.participant_reactions;
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "14px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, fontWeight: 700, color: "#93c5fd" }}>#{meetNum}</div>
          <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{h.zoom_url?.slice(0, 28)}{h.zoom_url?.length > 28 ? "…" : ""}</div>
        </div>
        <StatusBadge status={h.status} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.04)", borderRadius: 5, padding: "3px 8px" }}>
          👥 {h.bots_sent}/{h.total_bots || "—"}
        </span>
        {h.bots_failed > 0 && <span style={{ fontSize: 10, color: "#dc2626", background: "rgba(220,38,38,0.08)", borderRadius: 5, padding: "3px 8px" }}>✗ {h.bots_failed} failed</span>}
        <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.04)", borderRadius: 5, padding: "3px 8px" }}>{fmtTime(h.started_at)}</span>
        <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.04)", borderRadius: 5, padding: "3px 8px" }}>{nameLabel}</span>
        <span style={{ fontSize: 10, color: "#78716c", background: "rgba(255,255,255,0.04)", borderRadius: 5, padding: "3px 8px" }}>{typeLabel}</span>
        <span style={{ fontSize: 10, color: hasReact ? "#16a34a" : "#dc2626", background: hasReact ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.08)", borderRadius: 5, padding: "3px 8px" }}>
          {hasReact ? "✓ React" : "✗ React"}
        </span>
      </div>
    </div>
  );
}

/* ── Desktop Table Rows ── */
const CampRow = React.memo(function CampRow({ c, onStop, stopReason }) {
  const joined = c.bots_joined || c.active || 0;
  const total = c.total_bots || 1;
  const pct2 = Math.min(100, Math.round((joined / total) * 100));
  const isRun = ["running","joining","provisioning","pending"].includes(c.status);
  const nameLabel = c.name_type === "indian" ? "🇮🇳 Hindi" : c.name_type === "foreign" ? "🌍 English" : "✏️ Custom";
  const typeLabel = c.meeting_type === "webinar" ? "🎙 Webinar" : c.meeting_type === "profile_pic" ? "👤 Profile" : "👥 Normal";
  const hasReact = c.floating_emoji || c.participant_reactions;
  const startTime = c.started_at ? fmtTime(c.started_at) : c.schedule_at ? "⏰ " + fmtTime(c.schedule_at) : "—";
  return (
    <tr style={{ borderBottom: "1px solid rgba(255,255,255,.045)", background: isRun ? "rgba(180,83,9,0.03)" : "transparent" }}>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 700, color: "#9333ea", fontFamily: "'JetBrains Mono',monospace" }}>{c.username || "—"}</span></td>
      <td style={TDstyle}><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, fontWeight: 700, color: isRun ? "#b45309" : "#b45309", textShadow: isRun ? "0 0 10px rgba(180,83,9,0.7)" : "none" }}>{c.name || "—"}</span></td>
      <td style={TDstyle}><CopyUrlCell url={c.zoom_url} passcode={c.passcode} /></td>
      <td style={TDstyle}>
        <div style={{ fontSize: 13, color: "#292524", fontWeight: 700 }}>{joined}<span style={{ color: "#d6d3d1" }}>/{total}</span></div>
        {isRun && <div style={{ height: 3, background: "rgba(255,255,255,.06)", borderRadius: 3, marginTop: 5, overflow: "hidden", width: 56 }}><div style={{ height: "100%", width: pct2 + "%", background: "linear-gradient(90deg,#b45309,#7c3aed)", borderRadius: 3 }} /></div>}
      </td>
      <td style={TDstyle}><span style={{ fontSize: 10, color: "#292524", fontWeight: 600 }}>{startTime}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, color: "#292524", fontWeight: 600 }}>{c.leave_after_mins > 0 ? c.leave_after_mins + " min" : "∞ forever"}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, color: "#292524", fontWeight: 600 }}>{nameLabel}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, color: "#292524", fontWeight: 600 }}>{typeLabel}</span></td>
      <td style={TDstyle}>
        <span style={{ fontSize: 11, fontWeight: 700, color: hasReact ? "#16a34a" : "#dc2626", background: hasReact ? "rgba(22,163,74,.1)" : "rgba(220,38,38,.08)", border: "1px solid " + (hasReact ? "rgba(22,163,74,.25)" : "rgba(220,38,38,.2)"), borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>
          {hasReact ? "✓ Yes" : "✗ No"}
        </span>
      </td>
      <td style={TDstyle}>
        <StatusBadge status={c.status} />
        {!isRun && stopReason && <div style={{ fontSize: 9, color: "#dc2626", marginTop: 4, whiteSpace: "nowrap" }}>⚠ {fmtReason(stopReason)}</div>}
      </td>
      <td style={TDstyle}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {isRun ? <AB c="#dc2626" onClick={onStop}>⏹ Stop</AB> : <span style={{ fontSize: 11, color: "#d6d3d1" }}>—</span>}
        </div>
      </td>
    </tr>
  );
}, (prev, next) =>
  prev.c.id === next.c.id &&
  prev.c.status === next.c.status &&
  prev.c.bots_joined === next.c.bots_joined &&
  prev.c.active === next.c.active &&
  prev.c.active_bots === next.c.active_bots &&
  prev.stopReason === next.stopReason
);

const downloadAdminHistory = (history) => {
  const headers = ["User","Date","Task Name","Meeting URL / ID","Passcode","Members","Duration","Reactions","Status"];
  const rows = history.map(h => {
    const zurl = h.zoom_url||"";
    const idMatch = zurl.match(/zoom\.us\/j\/(\d+)/);
    const meetingCell = idMatch ? idMatch[1] : zurl;
    const hasReact = !!(h.floating_emoji || h.participant_reactions);
    return [
      h.username||"",
      new Date((h.started_at||0)*1000).toLocaleString(),
      h.campaign_name||h.name||"",
      meetingCell,
      h.passcode||"",
      h.bots_sent||0,
      (h.leave_after_mins||0)+"min",
      hasReact?"Yes":"No",
      h.status||""
    ];
  });
  const wsRows = rows.map(r => {
    const obj = {};
    headers.forEach((hh,i) => { obj[hh] = r[i]; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(wsRows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, "task_history.xlsx");
};

function HistoryRow({ h }) {
  const meetNum = h.campaign_name || h.name || "—";
  const nameLabel = h.name_type === "indian" ? "🇮🇳 Hindi" : h.name_type === "foreign" ? "🌍 English" : "✏️ Custom";
  const typeLabel = h.meeting_type === "webinar" ? "🎙 Webinar" : h.meeting_type === "profile_pic" ? "👤 Profile" : "👥 Normal";
  const hasReact = h.floating_emoji || h.participant_reactions;
  return (
    <tr style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 700, color: "#9333ea", fontFamily: "'JetBrains Mono',monospace" }}>{h.username || "—"}</span></td>
      <td style={TDstyle}><span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, fontWeight: 700, color: "#93c5fd", textShadow: "0 0 8px rgba(147,197,253,0.6)" }}>{meetNum}</span></td>
      <td style={TDstyle}><CopyUrlCell url={h.zoom_url} passcode={h.passcode} /></td>
      <td style={TDstyle}>
        <div style={{ fontSize: 13, color: "#1c1917", fontWeight: 800 }}>{h.bots_sent}<span style={{ color: "#78716c", fontWeight: 400 }}>/{h.total_bots || "—"}</span></div>
        {h.bots_failed > 0 && <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>✗ {h.bots_failed} failed</div>}
      </td>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 600, color: "#292524" }}>{fmtTime(h.started_at)}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 600, color: "#292524" }}>{h.leave_after_mins > 0 ? h.leave_after_mins + " min" : "∞ forever"}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 600, color: "#292524" }}>{nameLabel}</span></td>
      <td style={TDstyle}><span style={{ fontSize: 11, fontWeight: 600, color: "#292524" }}>{typeLabel}</span></td>
      <td style={TDstyle}>
        <span style={{ fontSize: 11, fontWeight: 700, color: hasReact ? "#16a34a" : "#dc2626", background: hasReact ? "rgba(22,163,74,.1)" : "rgba(220,38,38,.08)", border: `1px solid ${hasReact ? "rgba(22,163,74,.25)" : "rgba(220,38,38,.2)"}`, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>
          {hasReact ? "✓ Yes" : "✗ No"}
        </span>
      </td>
      <td style={TDstyle}><StatusBadge status={h.status} /></td>
      <td style={TDstyle}><span style={{ fontSize: 11, color: "#d6d3d1" }}>—</span></td>
    </tr>
  );
}

/* ── New Task helpers ── */
const FLOAT_EMOJIS = [
  { key: "clapping", emoji: "👏", label: "Clap" },
  { key: "thumbs_up", emoji: "👍", label: "Like" },
  { key: "joy", emoji: "😂", label: "Joy" },
  { key: "open_mouth", emoji: "😮", label: "Wow" },
  { key: "heart", emoji: "❤️", label: "Heart" },
];
const PART_REACTIONS = [
  { key: "yes", emoji: "✅", label: "Yes" },
  { key: "no", emoji: "❌", label: "No" },
  { key: "slow_down", emoji: "⏪", label: "Slow" },
  { key: "speed_up", emoji: "⏩", label: "Fast" },
  { key: "coffee", emoji: "☕", label: "Break" },
  { key: "raised_hand", emoji: "✋", label: "Hand" },
];
const NAME_OPTIONS = [
  { value: "indian", label: "🇮🇳 Indian Names" },
  { value: "foreign", label: "🌍 Foreign Names" },
  { value: "custom", label: "✏️ Custom Names" },
];
const MEETING_OPTIONS = [
  { value: "normal", label: "👥 Normal Participants" },
  // { value: "webinar", label: "🎙️ Webinar Participants" },
  // { value: "profile_pic", label: "👤 Profile Pic Participants" },
];
const EMPTY_FORM = {
  name: "", zoom_url: "", passcode: "", total_bots: 10, stagger_secs: 3,
  leave_after_mins: 0, join_timeout_secs: 300, name_type: "indian", custom_names: "",
  meeting_type: "normal", floating_emoji: false, float_emoji_keys: [], float_interval: 0, float_count: 0,
  participant_reactions: false, part_reaction_keys: [], part_count: 0, part_interval: 0,
  part_remove_after: 30, part_start_after: 0, schedule_enabled: false, schedule_at: "",
  slow_join: false, custom_spread_mins: "",
};

const inp = {
  width: "100%", background: "#ffffff", border: "1.5px solid #e7e5e4",
  color: "#1c1917", borderRadius: 9, padding: "9px 13px", fontSize: 13,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.18s, box-shadow 0.18s",
};
const lbl = {
  fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: "uppercase",
  color: "#78716c", display: "block", marginBottom: 5,
};

function NTDropdown({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = options.find(o => o.value === value);
  useEffect(() => {
    const fn = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "rgba(255,255,255,0.06)", border: `1px solid ${open ? "rgba(180,83,9,0.55)" : "rgba(255,255,255,0.13)"}`,
        color: "#44403c", borderRadius: 9, padding: "9px 13px", cursor: "pointer",
        fontSize: 13, fontFamily: "inherit", transition: "all 0.18s",
        boxShadow: open ? "0 0 0 3px rgba(180,83,9,0.09)" : "none", backdropFilter: "blur(8px)",
      }}>
        <span>{sel?.label || "Select…"}</span>
        <span style={{ fontSize: 9, color: "#78716c", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, zIndex: 999,
          background: "rgba(6,10,24,0.97)", border: "1px solid rgba(255,255,255,0.11)",
          borderRadius: 9, overflow: "hidden", backdropFilter: "blur(24px)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(180,83,9,0.1)",
        }}>
          {options.map(o => (
            <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} style={{
              padding: "10px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit",
              color: o.value === value ? "#b45309" : "#78716c",
              background: o.value === value ? "rgba(180,83,9,0.08)" : "transparent",
              borderLeft: `2px solid ${o.value === value ? "#b45309" : "transparent"}`, transition: "all 0.1s",
            }}
              onMouseEnter={e => { if (o.value !== value) { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "#292524"; } }}
              onMouseLeave={e => { if (o.value !== value) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#78716c"; } }}
            >{o.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function NTToggle({ label, checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "5px 0" }}>
      <div style={{
        width: 40, height: 22, borderRadius: 11, flexShrink: 0, position: "relative",
        background: checked ? "linear-gradient(135deg,#b45309,#7c3aed)" : "rgba(255,255,255,0.09)",
        border: checked ? "none" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: checked ? "0 0 14px rgba(180,83,9,0.4)" : "none", transition: "all 0.22s",
      }}>
        <div style={{
          position: "absolute", top: "50%", transform: "translateY(-50%)",
          left: checked ? 21 : 3, width: 16, height: 16, borderRadius: "50%",
          background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)"
        }} />
      </div>
      <span style={{ fontSize: 13, color: checked ? "#ffffff" : "#1c1917", fontWeight: 700, lineHeight: 1, userSelect: "none" }}>{label}</span>
    </div>
  );
}

function NTChips({ items, selected, onToggle }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {items.map(it => {
        const on = selected.includes(it.key);
        return (
          <button key={it.key} onClick={() => onToggle(it.key)} style={{
            border: `1px solid ${on ? "rgba(180,83,9,0.5)" : "rgba(255,255,255,0.1)"}`,
            background: on ? "rgba(180,83,9,0.13)" : "rgba(255,255,255,0.04)",
            color: on ? "#b45309" : "#44403c", borderRadius: 20, padding: "4px 12px",
            fontSize: 12, cursor: "pointer", fontFamily: "inherit", transition: "all 0.14s",
          }}>{it.emoji} {it.label}</button>
        );
      })}
    </div>
  );
}

function NTSmb({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", color: "#78716c",
      borderRadius: 6, padding: "4px 11px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", transition: "all .15s"
    }}>{children}</button>
  );
}

function NTModal({ onClose, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.88)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500
    }}>
      <div style={{
        background: "#ffffff", backdropFilter: "blur(28px)", border: "1px solid rgba(255,255,255,0.09)",
        borderRadius: 16, boxShadow: "0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",
        padding: 26, width: 400, maxWidth: "92vw",
      }}>{children}</div>
    </div>
  );
}

/* ── New Task Section ── */
function NewTaskSection({ onLaunched, isMobile }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [savedLists, setSavedLists] = useState(() => {
    try { return JSON.parse(localStorage.getItem("savedNameLists") || "{}"); } catch { return {}; }
  });
  const [listModal, setListModal] = useState(false);
  const [listName, setListName] = useState("");

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm(p => {
    const a = p[k] || [];
    return { ...p, [k]: a.includes(v) ? a.filter(x => x !== v) : [...a, v] };
  });

  async function submitCampaign() {
    setErr(""); setSuccess("");
    if (!form.name.trim()) return setErr("Task name required");
    if (!form.zoom_url.trim()) return setErr("Zoom URL required");
    if (form.name_type === "custom" && !form.custom_names.trim()) return setErr("Enter custom names");
    if (form.schedule_enabled && !form.schedule_at) return setErr("Set schedule time");
    if (form.slow_join && (!form.custom_spread_mins || +form.custom_spread_mins <= 0)) return setErr("Enter a valid Slow Join spread duration (minutes)");
    const body = {
      name: form.name, zoom_url: form.zoom_url.replace(/\s+/g, ""), passcode: form.passcode,
      total_bots: +form.total_bots, stagger_secs: +form.stagger_secs, leave_after_mins: parseInt(form.leave_after_mins, 10) || 0,
      custom_spread_mins: form.slow_join ? +form.custom_spread_mins : null,
      join_timeout_secs: +form.join_timeout_secs, name_type: form.name_type, custom_names: form.custom_names,
      meeting_type: form.meeting_type, floating_emoji: form.floating_emoji, participant_reactions: form.participant_reactions,
      reaction_schedule: (() => {
      let rs = [];
      if (form.floating_emoji && form.float_emoji_keys?.length > 0) {
        for (let i = 0; i < (+form.float_count || 1); i++) {
          form.float_emoji_keys.forEach(ek => rs.push({emoji: ek, delay_secs: i * (+form.float_interval || 0)}));
        }
      }
      return JSON.stringify(rs);
    })(),
      reaction_config: JSON.stringify({
        ...(form.participant_reactions ? {
          reactions: form.part_reaction_keys, count_per_wave: form.part_count,
          interval_secs: form.part_interval, remove_after: form.part_remove_after, start_after: form.part_start_after,
        } : {}),
        float_emoji_keys: form.float_emoji_keys,
        float_interval: +form.float_interval,
        float_count: +form.float_count,
      }),
      schedule_at: form.schedule_enabled ? new Date(form.schedule_at).getTime() / 1000 : null,
    };
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/campaigns", { method: "POST", body: JSON.stringify(body) });
      
      setSuccess(form.schedule_enabled ? "✦ Task scheduled!" : "✦ Task launched!");
      setForm(EMPTY_FORM);
      if (onLaunched) {
        onLaunched();
        setTimeout(onLaunched, 1000);
        setTimeout(onLaunched, 3000);
      }
    } catch (e) { setErr(e.message); } finally { setSubmitting(false); }
  }

  function saveList() {
    if (!listName.trim() || !form.custom_names.trim()) return;
    const u = { ...savedLists, [listName.trim()]: form.custom_names };
    setSavedLists(u); localStorage.setItem("savedNameLists", JSON.stringify(u));
    setListName(""); setListModal(false);
  }

  return (
    <>
      {err && (
        <div style={{ marginBottom: 12, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,.3)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#dc2626", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ flex: 1 }}>{err}</span>
          <span onClick={() => setErr("")} style={{ cursor: "pointer", opacity: .5, fontSize: 16 }}>✕</span>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 12, background: "rgba(21,128,61,0.07)", border: "1px solid rgba(21,128,61,.3)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#15803d", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ flex: 1 }}>{success}</span>
          <span onClick={() => setSuccess("")} style={{ cursor: "pointer", opacity: .5, fontSize: 16 }}>✕</span>
        </div>
      )}

      <div className="adm-card" style={{ marginBottom: 16, overflow: "visible" }}>
        <div className="adm-card-header" style={{ background: "rgba(180,83,9,0.025)" }}>
          <div className="adm-card-title">
            <span style={{ width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg,#b45309,#7c3aed)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 15, boxShadow: "0 0 16px rgba(180,83,9,.4)", flexShrink: 0 }}>✦</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#1c1917", letterSpacing: -.3 }}>New Task</div>
              <div style={{ fontSize: 9, color: "#78716c", letterSpacing: 2.2, textTransform: "uppercase" }}>CONFIGURE & LAUNCH</div>
            </div>
          </div>
        </div>

        <div style={{ padding: isMobile ? "16px" : "24px 26px" }}>

          {/* ROW 1 — Task Name | Meeting URL | Password */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Task Name</label>
              <input style={inp} placeholder="e.g. Monday Webinar" value={form.name} onChange={e => F("name", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Meeting URL / ID</label>
              <input style={inp} placeholder="https://zoom.us/j/... or ID" value={form.zoom_url} onChange={e => F("zoom_url", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Password (optional)</label>
              <input style={inp} placeholder="Leave blank if none" value={form.passcode} onChange={e => F("passcode", e.target.value)} />
            </div>
          </div>

          {/* ROW 2 — Member Names | Total Members | Leave After | Meeting Type */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "2fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={isMobile ? { gridColumn: "1 / -1" } : {}}>
              <label style={lbl}>Member Names</label>
              <NTDropdown options={NAME_OPTIONS} value={form.name_type} onChange={v => F("name_type", v)} />
            </div>
            <div>
              <label style={lbl}>Total Members</label>
              <input type="number" style={inp} min={10} step={10} value={form.total_bots} onChange={e => F("total_bots", e.target.value)} />
            </div>
            <div>
              <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>
                Leave After (min)
                <span title="0 = stay forever" style={{ width: 14, height: 14, borderRadius: "50%", background: "rgba(180,83,9,0.15)", border: "1px solid rgba(180,83,9,0.35)", color: "#b45309", fontSize: 9, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "help", flexShrink: 0 }}>i</span>
              </label>
              <input type="number" style={inp} min={0} value={form.leave_after_mins} onChange={e => F("leave_after_mins", e.target.value)} />
              <div style={{ fontSize: 10, color: "#a8a29e", marginTop: 3 }}>0 = stay forever</div>
            </div>
            <div style={isMobile ? { gridColumn: "1 / -1" } : {}}>
              <label style={lbl}>Meeting Type</label>
              <NTDropdown options={MEETING_OPTIONS} value={form.meeting_type} onChange={v => F("meeting_type", v)} />
            </div>
          </div>

          {/* ROW 3 — Custom names */}
          {form.name_type === "custom" && (
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Custom Names</label>
              <textarea style={{ ...inp, height: 78, resize: "vertical" }} placeholder={"John Doe\nJane Smith\n..."} value={form.custom_names} onChange={e => F("custom_names", e.target.value)} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontSize: 10, color: "#a8a29e" }}>{form.custom_names.split(/[\n,]/).filter(n => n.trim()).length} names</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <NTSmb onClick={() => setListModal("load")}>📁 Load</NTSmb>
                  <NTSmb onClick={() => { setListName(""); setListModal("save"); }}>💾 Save</NTSmb>
                </div>
              </div>
            </div>
          )}

          <div style={{ height: 1, background: "rgba(255,255,255,.06)", marginBottom: 20 }} />

          {/* ROW 4 — Reactions */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <NTToggle label="🎉 Floating Emoji Reactions" checked={form.floating_emoji} onChange={v => F("floating_emoji", v)} />
              {form.floating_emoji && (
                <div style={{ background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 10, padding: 14, marginTop: 10 }}>
                  <div style={{ fontSize: 9, color: "#1c1917", letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Pick Emojis</div>
                  <NTChips items={FLOAT_EMOJIS} selected={form.float_emoji_keys} onToggle={v => toggleArr("float_emoji_keys", v)} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                    <div><label style={lbl}>Interval (sec)</label><input type="number" style={inp} min={0} value={form.float_interval} onChange={e => F("float_interval", e.target.value)} /></div>
                    <div><label style={lbl}>Count (0 = rnd)</label><input type="number" style={inp} min={0} value={form.float_count} onChange={e => F("float_count", e.target.value)} /></div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <NTToggle label="📢 Participant Reactions" checked={form.participant_reactions} onChange={v => F("participant_reactions", v)} />
              {form.participant_reactions && (
                <div style={{ background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 10, padding: 14, marginTop: 10 }}>
                  <div style={{ fontSize: 9, color: "#1c1917", letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>Reactions</div>
                  <NTChips items={PART_REACTIONS} selected={form.part_reaction_keys} onToggle={v => toggleArr("part_reaction_keys", v)} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                    <div><label style={lbl}>Count/Wave</label><input type="number" style={inp} min={0} value={form.part_count} onChange={e => F("part_count", e.target.value)} /></div>
                    <div><label style={lbl}>Interval (sec)</label><input type="number" style={inp} min={0} value={form.part_interval} onChange={e => F("part_interval", e.target.value)} /></div>
                    <div><label style={lbl}>Remove After (sec)</label><input type="number" style={inp} min={0} value={form.part_remove_after} onChange={e => F("part_remove_after", e.target.value)} /></div>
                    <div><label style={lbl}>Start After (min)</label><input type="number" style={inp} min={0} value={form.part_start_after} onChange={e => F("part_start_after", e.target.value)} /></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,.06)", marginBottom: 20 }} />

          {/* ROW 4b — Slow Join */}
          <div style={{ marginBottom: 20 }}>
            <NTToggle label="🐢 Slow Join (spread members over time)" checked={form.slow_join} onChange={v => F("slow_join", v)} />
            {form.slow_join && (
              <div style={{ background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 10, padding: 14, marginTop: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={lbl}>Spread Over (minutes)</label>
                    <input type="number" style={inp} min={1} step={1} placeholder="e.g. 10" value={form.custom_spread_mins} onChange={e => F("custom_spread_mins", e.target.value)} />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <div style={{ fontSize: 11, color: "#78716c", lineHeight: 1.5 }}>
                      {(+form.custom_spread_mins > 0 && +form.total_bots > 0)
                        ? <>≈ {((+form.custom_spread_mins * 60) / +form.total_bots).toFixed(1)}s gap/member — all {form.total_bots} members join gradually over {form.custom_spread_mins} min instead of all at once.</>
                        : <>Enter minutes — e.g. 10 for "50 members over 10 min".</>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,.06)", marginBottom: 20 }} />

          {/* ROW 5 — Schedule + Launch */}
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-end", gap: 22 }}>
            <div style={{ flex: 1 }}>
              <NTToggle label="📅 Schedule Task (IST)" checked={form.schedule_enabled} onChange={v => F("schedule_enabled", v)} />
              {form.schedule_enabled && (
                <input type="datetime-local" style={{ ...inp, marginTop: 10, maxWidth: isMobile ? "100%" : 340 }} value={form.schedule_at} onChange={e => F("schedule_at", e.target.value)} />
              )}
            </div>
            <button
              onClick={submitCampaign}
              disabled={submitting}
              style={{
                flexShrink: 0, width: isMobile ? "100%" : "auto", minWidth: isMobile ? "unset" : 210,
                background: submitting ? "rgba(180,83,9,.28)" : "linear-gradient(135deg,#b45309,#7c3aed)",
                color: "#fff", border: "none", borderRadius: 11, padding: "14px 30px",
                fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer",
                fontFamily: "inherit", letterSpacing: .4,
                boxShadow: "0 4px 24px rgba(180,83,9,.3)", transition: "all .22s",
              }}
            >
              {submitting ? "Launching…" : form.schedule_enabled ? "⏰  Schedule Task" : "▶  Launch Task"}
            </button>
          </div>

        </div>
      </div>

      {listModal && (
        <NTModal onClose={() => setListModal(false)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1917" }}>{listModal === "save" ? "💾 Save Name List" : "📁 Saved Lists"}</div>
            <button onClick={() => setListModal(false)} style={{ background: "none", border: "none", color: "#a8a29e", fontSize: 20, cursor: "pointer" }}>✕</button>
          </div>
          {listModal === "save" ? (
            <>
              <input style={{ ...inp, marginBottom: 10 }} placeholder="List name…" value={listName} onChange={e => setListName(e.target.value)} />
              <button onClick={saveList} style={{ width: "100%", background: "linear-gradient(135deg,#b45309,#7c3aed)", color: "#fff", border: "none", borderRadius: 9, padding: "11px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
            </>
          ) : (
            Object.keys(savedLists).length === 0
              ? <div style={{ padding: "22px", textAlign: "center", color: "#78716c", fontSize: 12 }}>No saved lists</div>
              : Object.keys(savedLists).map(k => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
                  <span style={{ fontSize: 13, color: "#78716c", fontWeight: 500 }}>{k}</span>
                  <div style={{ display: "flex", gap: 7 }}>
                    <button onClick={() => { F("custom_names", savedLists[k]); setListModal(false); }} style={{ background: "rgba(180,83,9,.09)", border: "1px solid rgba(180,83,9,.22)", color: "#b45309", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Load</button>
                    <button onClick={() => { const u = { ...savedLists }; delete u[k]; setSavedLists(u); localStorage.setItem("savedNameLists", JSON.stringify(u)); }} style={{ background: "rgba(220,38,38,.07)", border: "1px solid rgba(220,38,38,.2)", color: "#dc2626", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Del</button>
                  </div>
                </div>
              ))
          )}
        </NTModal>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════
   BOTTOM NAV BAR (mobile)
══════════════════════════════════════════════ */

function BottomNav({ active, onChange, runningCount, schedCount }) {
  const tabs = [
    { id: "dashboard", icon: "◎", label: "Dashboard" },
    { id: "payments",  icon: "💳", label: "Payments" },
    { id: "broadcast", icon: "📢", label: "Broadcast" },
    { id: "admin",     icon: "⚙", label: "Admin" },
  ];

  const badges = { dashboard: runningCount, payments: null, broadcast: null, admin: schedCount };

  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      height: 70,
      background: "rgba(4,8,20,0.96)",
      backdropFilter: "blur(24px)",
      borderTop: "1px solid rgba(255,255,255,0.08)",
      display: "flex",
      alignItems: "stretch",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.5)",
    }}>
      {/* glow line top */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 1,
        background: "linear-gradient(90deg, transparent, rgba(180,83,9,0.4), rgba(124,58,237,0.4), transparent)",
      }} />

      {tabs.map(tab => {
        const isActive = active === tab.id;
        const badge = badges[tab.id];
        const glowColor = tab.id === "dashboard" ? "#b45309" : "#9333ea";
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 4, background: "none", border: "none", cursor: "pointer", position: "relative",
              transition: "all 0.22s", paddingBottom: 4,
              color: isActive ? glowColor : "#d6d3d1",
            }}
          >
            {/* active indicator bar */}
            {isActive && (
              <div style={{
                position: "absolute", top: 0, left: "20%", right: "20%", height: 2, borderRadius: "0 0 2px 2px",
                background: `linear-gradient(90deg, transparent, ${glowColor}, transparent)`,
                boxShadow: `0 0 8px ${glowColor}`,
              }} />
            )}

            {/* icon */}
            <div style={{
              width: 34, height: 34, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18,
              background: isActive ? `${glowColor}18` : "transparent",
              boxShadow: isActive ? `0 0 14px ${glowColor}40` : "none",
              transition: "all 0.22s",
              transform: isActive ? "translateY(-1px)" : "none",
              position: "relative",
            }}>
              {tab.icon}
              {badge > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4,
                  minWidth: 16, height: 16, borderRadius: 8,
                  background: glowColor, color: "#000",
                  fontSize: 9, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 4px",
                  boxShadow: `0 0 8px ${glowColor}80`,
                }}>{badge}</span>
              )}
            </div>

            {/* label */}
            <span style={{
              fontSize: 10, fontWeight: isActive ? 800 : 500,
              letterSpacing: isActive ? 0.8 : 0.3,
              textTransform: "uppercase",
              transition: "all 0.22s",
            }}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════ */
export default function Dashboard() {
  const stoppedCampaignsRef = useRef(new Set());
  const [campaigns, setCampaigns] = useState([]);
  const [agents,    setAgents]    = useState([]);
  const [events,    setEvents]    = useState(() => { try { const s = sessionStorage.getItem("terminal_logs"); return s ? JSON.parse(s) : []; } catch { return []; } });
  const [scheduled, setScheduled] = useState([]);
  const [history,   setHistory]   = useState([]);
  const [, setTick] = useState(0);
  const [cPage, setCPage] = useState(0);
  const [hPage, setHPage] = useState(0);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isMobile, setIsMobile] = useState(false);
  const [stopReasons, setStopReasons] = useState({});
  const PS = 4;
  const logRef = useRef(null);

  /* detect mobile */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const load = useCallback(async () => {
    try {
      const [c, a, s, h] = await Promise.all([
        apiFetch("/api/campaigns"),
        apiFetch("/api/agents"),
        apiFetch("/api/campaigns/scheduled/all"),
        apiFetch("/api/campaigns/history/all"),
      ]);
      // Sirf dynamic fields update karo — static fields preserve karo (no flicker)
      if (c?.length >= 0) setCampaigns(prev => {
        const prevMap = Object.fromEntries(prev.map(x => [x.id, x]));
        const serverMap = Object.fromEntries(c.map(x => [x.id, x]));
        const activeIds = new Set(c.map(x => x.id));
        const updated = prev.map(p => {
          const s = serverMap[p.id];
          if (!s) return p; // server ne nahi bheja — rakho
          const ASTAT = ["running","joining","provisioning","pending"];
          const wasActive = ASTAT.includes(p.status);
          const nowActive = ASTAT.includes(s.status);
          // Active campaign ko KABHI non-active mat karo via load() — sirf local stop se
          if (wasActive && !nowActive && !stoppedCampaignsRef.current.has(p.id)) return p;
          // Same reference — no re-render if nothing changed
          if (p.status === s.status && p.active_bots === s.active_bots &&
              p.bots_joined === s.bots_joined && p.active === s.active) return p;
          return { ...p, status: s.status, active_bots: s.active_bots, bots_joined: s.bots_joined, active: s.active, total_bots: s.total_bots };
        });
        const added = c.filter(x => !prevMap[x.id]);
        // Sirf stopped/finished campaigns hatao — active wale kabhi remove mat karo
        const filtered = updated.filter(p => {
          if (activeIds.has(p.id)) return true;
          return ["running","joining","provisioning","pending"].includes(p.status);
        });
        const merged2 = [...filtered, ...added];
        // Order stable rakho — id se sort karo flicker avoid ke liye
        merged2.sort((a, b) => (a.created_at || 0) > (b.created_at || 0) ? -1 : 1);
        return merged2;
      });
      // DB se stop_reason merge karo — page refresh pe bhi dikhega
      if (c?.length >= 0) {
        const reasons = {};
        c.forEach(x => { if (x.stop_reason) reasons[x.id] = x.stop_reason; });
        if (Object.keys(reasons).length > 0) setStopReasons(p => ({ ...p, ...reasons }));
      }
      if (a?.length >= 0) setAgents(a);
      if (s?.length >= 0) setScheduled(s);
      // Backend already caps history at 200 rows; slicing to 50 here hid
      // anything older than ~16h now that ~75 campaigns run per day.
      if (h?.length >= 0) setHistory(h);
    } catch {}
  }, []);

  
  // Debounced load — 50 bots ek saath update kare to sirf 1 load hoga
  const loadDebouncedRef = useRef(null);
  const loadDebounced = useCallback(() => {
    if (loadDebouncedRef.current) clearTimeout(loadDebouncedRef.current);
    loadDebouncedRef.current = setTimeout(() => { load(); }, 1500);
  }, [load]);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  // Auto-refresh when host ends meeting (campaign_auto_stopped event)
  useEffect(() => {
    const WS_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/^http/, "ws");
    let ws = null;
    let reconnectTimer = null;
    let dead = false;

    const SIOURL = (process.env.REACT_APP_API_URL || window.location.origin);
    let sioScript = document.getElementById("sio-cdn");
    function initSio() {
      if (dead) return;
      try {
        ws = window.io(SIOURL, { transports: ["websocket", "polling"], reconnectionDelay: 1000, reconnectionDelayMax: 3000 });
        ws.on("connect", () => {
          ws.emit("join_dashboard", {});
        });
        ws.on("campaign_auto_stopped", (d) => { if (d?.campaign_id && d?.reason) setStopReasons(p => ({...p, [d.campaign_id]: d.reason})); loadDebounced(); });
        ws.on("bot_update", (d) => {
          if (d && d.campaign_id) {
            setCampaigns(prev => prev.map(c => {
              if (c.id !== d.campaign_id) return c;
              // bot_update.status = BOT ka status (joined/left/error) — campaign status mat badlo!
              // Campaign status sirf status_update ya campaign_update se update hoga
              const nBJ = d.bots_joined ?? c.bots_joined;
              const nAB = d.active_bots ?? c.active_bots;
              // Same ref — no re-render if nothing changed
              if (c.bots_joined === nBJ && c.active_bots === nAB) return c;
              return { ...c, bots_joined: nBJ, active_bots: nAB, active: nBJ };
            }));
          }
        });
        ws.on("campaign_update", (d) => {
          if (d && d.campaign_id) {
            setCampaigns(prev => prev.map(c => {
              if (c.id !== d.campaign_id) return c;
              const ASTAT = ["running","joining","provisioning","pending"];
              // Active campaign ko non-active mat karo
              const newStatus = d.status || c.status;
              if (ASTAT.includes(c.status) && !ASTAT.includes(newStatus)) return c;
              const nBJ = d.bots_joined ?? c.bots_joined;
              const nAB = d.active_bots ?? c.active_bots;
              const nAct = d.active ?? nBJ ?? c.active;
              // Same ref — no flicker
              if (c.status === newStatus && c.bots_joined === nBJ &&
                  c.active_bots === nAB && c.active === nAct) return c;
              return { ...c, status: newStatus, bots_joined: nBJ, active_bots: nAB, active: nAct };
            }));
          } else { loadDebounced(); }
        });
        // Agent offline counter — sirf 3 consecutive offline updates ke baad offline dikhao
        const agentOfflineCount = agentOfflineCountRef.current;
        ws.on("status_update", (evData) => {
          if (evData.campaigns) {
            setCampaigns(prev => {
              const updMap = Object.fromEntries(evData.campaigns.map(u => [u.id, u]));
              const hasNew = evData.campaigns.some(u => !prev.find(c => c.id === u.id));
              // hasNew — new campaigns add karo + existing bhi update karo
              if (hasNew) {
                const newOnes = evData.campaigns.filter(u => !prev.find(c => c.id === u.id));
                const ACTIVE2 = ["running","joining","provisioning","pending"];
                const updatedPrev = prev.map(c => {
                  const upd = updMap[c.id];
                  if (!upd) return c;
                  if (ACTIVE2.includes(c.status) && !ACTIVE2.includes(upd.status)) return c;
                  return { ...c, status: upd.status ?? c.status, active_bots: upd.active_bots ?? c.active_bots, bots_joined: upd.bots_joined ?? c.bots_joined, active: upd.active ?? c.active };
                });
                return [...updatedPrev, ...newOnes];
              }
              const ACTIVE = ["running","joining","provisioning","pending"];
              const _mergeOne = (c) => {
                const upd = updMap[c.id];
                if (!upd) return c; // Server se nahi aai — rakho
                if (stoppedCampaignsRef.current.has(c.id)) return c;
                // KEY FIX: Active campaign ko non-active mat karo via status_update
                // Sirf local stop ya load() API se hi stop hona chahiye
                if (ACTIVE.includes(c.status) && !ACTIVE.includes(upd.status)) return c;
                if (c.status === "stopped" && upd.status !== "stopped") return c;
                const nStatus = upd.status ?? c.status;
                const nActive_bots = upd.active_bots ?? c.active_bots;
                const nBots_joined = upd.bots_joined ?? c.bots_joined;
                const nActive = upd.active ?? c.active;
                // Same reference return karo — no re-render if nothing changed
                if (c.status === nStatus && c.active_bots === nActive_bots &&
                    c.bots_joined === nBots_joined && c.active === nActive) return c;
                return { ...c, status: nStatus, active_bots: nActive_bots, bots_joined: nBots_joined, active: nActive };
              };
              return prev.map(_mergeOne);
            });
          }
          if (evData.agents) {
            setAgents(prev => {
              return evData.agents.map(newAgent => {
                const existing = prev.find(a => a.id === newAgent.id);
                // Smoothing: agar agent online tha aur ab offline aa raha hai
                // to 3 consecutive offline updates ke baad hi offline dikhao
                if (existing && existing.status === "online" && newAgent.status === "offline") {
                  agentOfflineCount[newAgent.id] = (agentOfflineCount[newAgent.id] || 0) + 1;
                  if (agentOfflineCount[newAgent.id] < 3) {
                    // Abhi online rakho — temporary blip ignore karo
                    return { ...newAgent, status: "online" };
                  }
                } else if (newAgent.status === "online") {
                  // Online aa gaya — counter reset karo
                  agentOfflineCount[newAgent.id] = 0;
                }
                return newAgent;
              });
            });
          }
        });
        ws.on("log", (evData) => {
          setEvents(prev => { const next = [...prev, { ts: evData.ts, level: evData.level, source: evData.source, message: evData.message }].slice(-200); try { sessionStorage.setItem('terminal_logs', JSON.stringify(next)); } catch {} return next; });
        });
        ws.on("disconnect", () => { if (!dead) setTimeout(() => { try { ws.connect(); } catch {} }, 1000); });
      } catch {}
    }
    if (!sioScript) {
      sioScript = document.createElement("script");
      sioScript.id = "sio-cdn";
      sioScript.src = "https://cdn.socket.io/4.7.5/socket.io.min.js";
      sioScript.onload = initSio;
      document.head.appendChild(sioScript);
    } else if (window.io) {
      initSio();
    } else {
      sioScript.onload = initSio;
    }
    return () => {
      dead = true;
      try { sio && sio.disconnect(); } catch {}
    };
  }, [load]);
  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [events.length]);

  const running      = campaigns.filter(c => ["running", "joining", "provisioning", "pending"].includes(c.status));
  const totalBots    = running.reduce((a, c) => a + (c.total_bots || 0), 0);
  const onlineAgents = agents.filter(a => a.status === "online").length;
  const agentCap     = agents.reduce((a, ag) => a + (ag.status === "online" ? ag.capacity : 0), 0);
  const activeSched  = scheduled.filter(s => s.status === "waiting");
  const activeCampaigns = campaigns.filter(c => ["running", "joining", "provisioning", "pending"].includes(c.status));
  const pagedC = activeCampaigns.slice(cPage * PS, (cPage + 1) * PS);
  const pagedH = history.slice(hPage * PS, (hPage + 1) * PS);

  const [confirmStop, setConfirmStop] = useState(null);
  const doStop  = useCallback(async id => { setConfirmStop(id); }, []);
  const doStopConfirmed = async id => {
    setConfirmStop(null);
    stoppedCampaignsRef.current.add(id);
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: "stopped", active_bots: 0, active: 0, bots_joined: 0 } : c));
    try { await apiFetch(`/api/campaigns/${id}/stop`, { method: "POST" }); } catch {}
    setTimeout(() => { stoppedCampaignsRef.current.delete(id); load(); }, 2000);
  };
  const [confirmForce, setConfirmForce] = useState(null);
  const doForce = useCallback(async id => { setConfirmForce(id); }, []);
  const doForceConfirmed = async id => {
    setConfirmForce(null);
    stoppedCampaignsRef.current.add(id);
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: "stopped", active_bots: 0, active: 0, bots_joined: 0 } : c));
    try { await apiFetch(`/api/campaigns/${id}/force_exit`, { method: "POST" }); } catch {}
    setTimeout(() => { stoppedCampaignsRef.current.delete(id); load(); }, 2000);
  };
  const doCancel= async id => { try { await apiFetch(`/api/campaigns/scheduled/${id}/cancel`, { method: "POST" }); load(); } catch {} };

  const stats = [
    { icon: "◎", label: "Bots in Meetings",  val: totalBots,          c: "#16a34a", k: "green"  },
    { icon: "●", label: "Running Campaigns", val: running.length,      c: "#b45309", k: "blue"   },
    { icon: "◈", label: "Online Servers",     val: onlineAgents,        c: "#d97706", k: "amber"  },
    { icon: "◷", label: "Scheduled Tasks",   val: activeSched.length,  c: "#9333ea", k: "purple" },
  ];

  /* ── Shared content blocks ── */
  const dashboardContent = (
    <>
      {/* Stats grid */}
      <div className="adm-stat-grid" style={isMobile ? { gridTemplateColumns: "1fr 1fr", gap: 10 } : {}}>
        {stats.map((s, i) => (
          <div key={i} className="adm-stat-card" data-c={s.k} style={{ animationDelay: `${i * 0.07}s` }}>
            <div className="adm-stat-bar" style={{ background: `linear-gradient(90deg,transparent,${s.c},transparent)`, boxShadow: `0 0 14px ${s.c}80` }} />
            <div className="adm-stat-watermark" style={{ color: s.c }}>{s.icon}</div>
            <div className="adm-stat-val" style={{ color: s.c, fontSize: isMobile ? 24 : undefined }}>{s.val.toLocaleString()}</div>
            <div className="adm-stat-lbl" style={isMobile ? { fontSize: 10 } : {}}>{s.label}</div>
            {i === 0 && totalBots > 0 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", boxShadow: "0 0 7px #16a34a", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, color: "#44403c", letterSpacing: .5 }}>{running.length} active</span>
              </div>
            )}
            {i === 2 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#d97706", boxShadow: "0 0 7px #d97706", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: "#1c1917", letterSpacing: .5 }}>{agentCap} cap</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New Task */}
      <NewTaskSection onLaunched={load} isMobile={isMobile} />

      {/* All Meetings */}
      <div className="adm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#b45309", boxShadow: "0 0 10px #b45309" }} />
            All Meetings
            {activeCampaigns.length > 0 && (
              <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, marginLeft: 8 }}>{activeCampaigns.length}</span>
            )}
          </div>
          <Pager page={cPage} total={Math.ceil(activeCampaigns.length / PS)} set={setCPage} />
        </div>
        {activeCampaigns.length === 0
          ? <div className="adm-empty">No meetings yet — launch a task above ↑</div>
          : isMobile
            ? <div style={{ padding: "12px" }}>{pagedC.map(c => <MobileCampCard key={c.id} c={c} onStop={() => doStop(c.id)} onForce={() => doForce(c.id)} stopReason={stopReasons[c.id]} />)}</div>
            : <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                      {["User", "Task Name", "URL / ID", "Qty", "Start Time", "Leave After", "Name", "Type", "React", "Status", "Action"].map(h => (
                        <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1c1917", whiteSpace: "nowrap", fontWeight: 800, textShadow: "0 0 8px rgba(28,25,23,0.6)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{pagedC.map(c => <CampRow key={c.id} c={c} onStop={() => doStop(c.id)} onForce={() => doForce(c.id)} stopReason={stopReasons[c.id]} />)}</tbody>
                </table>
              </div>
        }
      </div>

      {/* Previous Tasks */}
      <div className="adm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#7c3aed", boxShadow: "0 0 10px #7c3aed" }} />
            Previous Tasks
            {history.length > 0 && (
              <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, marginLeft: 8 }}>{history.length}</span>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {history.length>0&&<button onClick={()=>downloadAdminHistory(history)} style={{ background:"rgba(180,83,9,0.1)",border:"1px solid rgba(180,83,9,0.3)",color:"#b45309",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>⬇ Download</button>}
            <Pager page={hPage} total={Math.ceil(history.length / PS)} set={setHPage} />
          </div>
        </div>
        {history.length === 0
          ? <div className="adm-empty">No history yet</div>
          : isMobile
            ? <div style={{ padding: "12px" }}>{pagedH.map(h => <MobileHistoryCard key={h.id} h={h} />)}</div>
            : <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                      {["User", "Task Name", "URL / ID", "Qty", "Start Time", "Leave After", "Name", "Type", "React", "Status", "Action"].map(col => (
                        <th key={col} style={{ padding: "9px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1c1917", whiteSpace: "nowrap", fontWeight: 800, textShadow: "0 0 8px rgba(28,25,23,0.6)" }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{pagedH.map(h => <HistoryRow key={h.id} h={h} />)}</tbody>
                </table>
              </div>
        }
      </div>

      {/* Agent Node Load */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#d97706", boxShadow: "0 0 10px #d97706" }} />
            Agent Node Load
          </div>
          <span style={{ fontSize: 11, color: "#a8a29e" }}>{onlineAgents}/{agents.length} online</span>
        </div>
        <div style={{ padding: "8px 0" }}>
          {agents.length === 0
            ? <div className="adm-empty">No Server registered yet</div>
            : agents.map(a => {
                const p = pct(a.active_bots, a.capacity);
                return (
                  <div key={a.id} style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#292524" }}>{a.name}</span>
                        {a.status === "online" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", boxShadow: "0 0 7px #16a34a", animation: "pulse 2s infinite" }} />}
                      </div>
                      <span style={{ fontSize: 12, color: "#44403c", fontWeight: 600 }}>
                        {a.status === "online"
                          ? <><span style={{ color: "#16a34a", fontWeight: 700 }}>{a.active_bots}</span> / {a.capacity}</>
                          : a.status}
                      </span>
                    </div>
                    {a.status === "online" && (
                      <div className="adm-prog">
                        <div className="adm-prog-fill" style={{ width: p + "%", background: p >= 90 ? "#dc2626" : p >= 70 ? "#d97706" : "#b45309" }} />
                      </div>
                    )}
                  </div>
                );
              })
          }
        </div>
      </div>
      {/* Campaign Terminal */}
      <div style={{ margin: "18px 0 0 0", background: "#f4f3f0", border: "1px solid #e7e5e4", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e7e5e4", background: "#f4f3f0" }}>
          <span style={{ color: "#b45309", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>⬛ CAMPAIGN TERMINAL</span>
          <button onClick={() => { setEvents([]); try { sessionStorage.removeItem("terminal_logs"); } catch {} }} style={{ background: "none", border: "1px solid #d6d3d1", color: "#78716c", borderRadius: 6, padding: "2px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
        </div>
        <div ref={logRef} style={{ height: 220, overflowY: "auto", padding: "8px 12px", fontFamily: "monospace", fontSize: 11, display: "flex", flexDirection: "column" }}>
          {events.length === 0 && <div style={{ color: "#d6d3d1", padding: "60px 0", textAlign: "center" }}>Waiting for events...</div>}
          {events.map((e, i) => (
            <div key={i} style={{ padding: "2px 0", borderBottom: "1px solid #f4f3f0", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "#a8a29e", minWidth: 70 }}>{new Date(e.ts * 1000).toLocaleTimeString()}</span>
              <span style={{ minWidth: 70, color: e.source === "provision" ? "#7c3aed" : e.source === "agent" ? "#15803d" : "#f59e0b", fontWeight: 600 }}>[{e.source}]</span>
              <span style={{ color: e.level === "warn" ? "#d97706" : e.level === "error" ? "#dc2626" : "#44403c", flex: 1 }}>{e.message}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const adminContent = (
    <>
      {/* Scheduled Tasks */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#9333ea", boxShadow: "0 0 10px #9333ea" }} />
            Scheduled Tasks
          </div>
          {activeSched.length > 0 && (
            <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{activeSched.length}</span>
          )}
        </div>
        {activeSched.length === 0
          ? <div className="adm-empty">No scheduled tasks</div>
          : activeSched.map(s => (
            <div key={s.id} style={{ padding: "13px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#44403c", marginBottom: 3 }}>{s.campaign_name}</div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ fontSize: 11, color: "#d97706", fontFamily: "'JetBrains Mono',monospace" }}>⏱ {fmtCountdown(s.scheduled_at)}</div>
                  {s.total_bots&&<span style={{ fontSize:11,color:"#b45309",background:"rgba(180,83,9,.08)",border:"1px solid rgba(180,83,9,.22)",padding:"2px 8px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace" }}>👥 {s.total_bots} members</span>}
                </div>
              </div>
              <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => doCancel(s.id)}>✕ Cancel</button>
            </div>
          ))
        }
      </div>
    </>
  );

/* ── Mobile layout with bottom nav ── */
if (isMobile) {
  return (
    <div style={{ animation: "floatUp .35s ease", paddingBottom: 80 }}>
      {activeTab === "dashboard" && dashboardContent}
      {activeTab === "payments"  && <Payments />}
      {activeTab === "broadcast" && <Broadcast />}
      {activeTab === "admin"     && (
        <div style={{ animation: "floatUp .35s ease" }}>
          {/* Scheduled Tasks */}
          {adminContent}

          {/* Full Admin Panel (User Management + Login History) */}
          <Admin currentUser={localStorage.getItem("username") || ""} />
        </div>
      )}
      <BottomNav
        active={activeTab}
        onChange={setActiveTab}
        runningCount={running.length}
        schedCount={activeSched.length}
      />
      <ConfirmModal
        open={!!confirmForce}
        title="Force Exit All Bots?"
        message="This will immediately disconnect all bots from the meeting. Are you sure?"
        confirmLabel="Force Exit"
        onConfirm={() => doForceConfirmed(confirmForce)}
        onCancel={() => setConfirmForce(null)}
      />
      <ConfirmModal
        open={!!confirmStop}
        title="Stop Campaign?"
        message={`Are you sure you want to stop "${campaigns.find(c => c.id === confirmStop)?.name || ''}"?`}
        confirmLabel="Stop"
        confirmColor="#dc2626"
        onConfirm={() => doStopConfirmed(confirmStop)}
        onCancel={() => setConfirmStop(null)}
      />
    </div>
  );
}

  /* ── Desktop layout ── */
  return (
    <div style={{ animation: "floatUp .35s ease" }}>

      {/* Stats */}
      <div className="adm-stat-grid">
        {stats.map((s, i) => (
          <div key={i} className="adm-stat-card" data-c={s.k} style={{ animationDelay: `${i * 0.07}s` }}>
            <div className="adm-stat-bar" style={{ background: `linear-gradient(90deg,transparent,${s.c},transparent)`, boxShadow: `0 0 14px ${s.c}80` }} />
            <div className="adm-stat-watermark" style={{ color: s.c }}>{s.icon}</div>
            <div className="adm-stat-val" style={{ color: s.c }}>{s.val.toLocaleString()}</div>
            <div className="adm-stat-lbl">{s.label}</div>
            {i === 0 && totalBots > 0 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", boxShadow: "0 0 7px #16a34a", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, color: "#44403c", letterSpacing: .5 }}>{running.length} campaign{running.length !== 1 ? "s" : ""} active</span>
              </div>
            )}
            {i === 2 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#d97706", boxShadow: "0 0 7px #d97706", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1c1917", letterSpacing: .5 }}>{agentCap} bot capacity</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <NewTaskSection onLaunched={load} isMobile={false} />

      {/* All Meetings Table */}
      <div className="adm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#b45309", boxShadow: "0 0 10px #b45309" }} />
            All Meetings
            {activeCampaigns.length > 0 && (
              <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, marginLeft: 8 }}>{activeCampaigns.length}</span>
            )}
          </div>
          <Pager page={cPage} total={Math.ceil(activeCampaigns.length / PS)} set={setCPage} />
        </div>
        {activeCampaigns.length === 0
          ? <div className="adm-empty">No meetings yet — launch a task above ↑</div>
          : <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                    {["User", "Task Name", "URL / ID", "Qty", "Start Time", "Leave After", "Name", "Type", "React", "Status", "Action"].map(h => (
                      <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1c1917", whiteSpace: "nowrap", fontWeight: 800, textShadow: "0 0 8px rgba(28,25,23,0.6)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>{pagedC.map(c => <CampRow key={c.id} c={c} onStop={() => doStop(c.id)} onForce={() => doForce(c.id)} stopReason={stopReasons[c.id]} />)}</tbody>
              </table>
            </div>
        }
      </div>

      {/* Agent Node Load */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#d97706", boxShadow: "0 0 10px #d97706" }} />
            Agent Node Load
          </div>
          <span style={{ fontSize: 11, color: "#a8a29e" }}>{onlineAgents}/{agents.length} online</span>
        </div>
        <div style={{ padding: "8px 0" }}>
          {agents.length === 0
            ? <div className="adm-empty">No Server registered yet</div>
            : agents.map(a => {
                const p = pct(a.active_bots, a.capacity);
                return (
                  <div key={a.id} style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#292524" }}>{a.name}</span>
                        {a.status === "online" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", boxShadow: "0 0 7px #16a34a", animation: "pulse 2s infinite" }} />}
                      </div>
                      <span style={{ fontSize: 12, color: "#44403c", fontWeight: 600 }}>
                        {a.status === "online" ? <><span style={{ color: "#16a34a", fontWeight: 700 }}>{a.active_bots}</span> / {a.capacity}</> : a.status}
                      </span>
                    </div>
                    {a.status === "online" && (
                      <div className="adm-prog">
                        <div className="adm-prog-fill" style={{ width: p + "%", background: p >= 90 ? "#dc2626" : p >= 70 ? "#d97706" : "#b45309" }} />
                      </div>
                    )}
                  </div>
                );
              })
          }
        </div>
      </div>

      {/* Scheduled Tasks */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#9333ea", boxShadow: "0 0 10px #9333ea" }} />
            Scheduled Tasks
          </div>
          {activeSched.length > 0 && (
            <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{activeSched.length}</span>
          )}
        </div>
        {activeSched.length === 0
          ? <div className="adm-empty">No scheduled tasks</div>
          : activeSched.map(s => (
            <div key={s.id} style={{ padding: "13px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#44403c", marginBottom: 3 }}>{s.campaign_name}</div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ fontSize: 11, color: "#d97706", fontFamily: "'JetBrains Mono',monospace" }}>⏱ {fmtCountdown(s.scheduled_at)}</div>
                  {s.total_bots&&<span style={{ fontSize:11,color:"#b45309",background:"rgba(180,83,9,.08)",border:"1px solid rgba(180,83,9,.22)",padding:"2px 8px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace" }}>👥 {s.total_bots} members</span>}
                </div>
              </div>
              <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => doCancel(s.id)}>✕ Cancel</button>
            </div>
          ))
        }
      </div>

      {/* Previous Tasks */}
      <div className="adm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div className="adm-card-header">
          <div className="adm-card-title">
            <span className="adm-dot" style={{ background: "#7c3aed", boxShadow: "0 0 10px #7c3aed" }} />
            Previous Tasks
            {history.length > 0 && (
              <span style={{ background: "rgba(255,255,255,.07)", color: "#78716c", border: "1px solid rgba(255,255,255,.09)", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, marginLeft: 8 }}>{history.length}</span>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {history.length>0&&<button onClick={()=>downloadAdminHistory(history)} style={{ background:"rgba(180,83,9,0.1)",border:"1px solid rgba(180,83,9,0.3)",color:"#b45309",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>⬇ Download</button>}
            <Pager page={hPage} total={Math.ceil(history.length / PS)} set={setHPage} />
          </div>
        </div>
        {history.length === 0
          ? <div className="adm-empty">No history yet</div>
          : <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 750 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                    {["User", "Task Name", "URL / ID", "Qty", "Start Time", "Leave After", "Name", "Type", "React", "Status", "Action"].map(col => (
                      <th key={col} style={{ padding: "9px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1c1917", whiteSpace: "nowrap", fontWeight: 800, textShadow: "0 0 8px rgba(28,25,23,0.6)" }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>{pagedH.map(h => <HistoryRow key={h.id} h={h} />)}</tbody>
              </table>
            </div>
        }
      </div>
      {/* Campaign Terminal */}
      <div style={{ margin: "18px 0 16px 0", background: "#f4f3f0", border: "1px solid #e7e5e4", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e7e5e4", background: "#f4f3f0" }}>
          <span style={{ color: "#b45309", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>⬛ CAMPAIGN TERMINAL</span>
          <button onClick={() => { setEvents([]); try { sessionStorage.removeItem("terminal_logs"); } catch {} }} style={{ background: "none", border: "1px solid #d6d3d1", color: "#78716c", borderRadius: 6, padding: "2px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
        </div>
        <div ref={logRef} style={{ height: 220, overflowY: "auto", padding: "8px 12px", fontFamily: "monospace", fontSize: 11, display: "flex", flexDirection: "column" }}>
          {events.length === 0 && <div style={{ color: "#d6d3d1", padding: "60px 0", textAlign: "center" }}>Waiting for events...</div>}
          {events.map((e, i) => (
            <div key={i} style={{ padding: "2px 0", borderBottom: "1px solid #f4f3f0", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "#a8a29e", minWidth: 70 }}>{new Date(e.ts * 1000).toLocaleTimeString()}</span>
              <span style={{ minWidth: 70, color: e.source === "provision" ? "#7c3aed" : e.source === "agent" ? "#15803d" : "#f59e0b", fontWeight: 600 }}>[{e.source}]</span>
              <span style={{ color: e.level === "warn" ? "#d97706" : e.level === "error" ? "#dc2626" : "#44403c", flex: 1 }}>{e.message}</span>
            </div>
          ))}
        </div>
      </div>
      <ConfirmModal
        open={!!confirmForce}
        title="Force Exit All Bots?"
        message="This will immediately disconnect all bots from the meeting. Are you sure?"
        confirmLabel="Force Exit"
        onConfirm={() => doForceConfirmed(confirmForce)}
        onCancel={() => setConfirmForce(null)}
      />
      <ConfirmModal
        open={!!confirmStop}
        title="Stop Campaign?"
        message={`Are you sure you want to stop "${campaigns.find(c => c.id === confirmStop)?.name || ''}"?`}
        confirmLabel="Stop"
        confirmColor="#dc2626"
        onConfirm={() => doStopConfirmed(confirmStop)}
        onCancel={() => setConfirmStop(null)}
      />

    </div>
  );
}